<template>
  <div class="settings-page">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="模型与提供商" name="llm">
        <el-alert
          type="info"
          :closable="false"
          show-icon
          class="hint"
          title="配置各业务流程使用的 LLM 提供商与模型。保存后写入数据库，刷新页面不会丢失。"
        />

        <div class="toolbar">
          <el-button :icon="Refresh" @click="loadLlm" :loading="loading">刷新</el-button>
          <el-button type="primary" :icon="Check" @click="saveLlm" :loading="saving">保存全部</el-button>
        </div>

        <div v-loading="loading">
          <template v-for="cat in categories" :key="cat">
            <div class="cat-title">{{ cat }}</div>
            <el-card
              v-for="row in itemsByCategory(cat)"
              :key="row.workflow_key"
              class="wf-card"
              shadow="never"
            >
              <div class="wf-head">
                <div>
                  <b>{{ row.label || row.workflow_key }}</b>
                  <span class="wf-key">{{ row.workflow_key }}</span>
                </div>
                <el-switch v-model="row.is_enabled" active-text="启用" />
              </div>
              <p v-if="row.description" class="wf-desc">{{ row.description }}</p>

              <!-- 多提供商回退 -->
              <template v-if="row.is_priority_list">
                <el-form label-width="100px" size="default">
                  <el-form-item label="回退顺序">
                    <el-select
                      v-model="row.extra_json.provider_priority"
                      multiple
                      filterable
                      style="width: 100%"
                      placeholder="按顺序尝试"
                    >
                      <el-option
                        v-for="p in providerOptions"
                        :key="p.id"
                        :label="p.label"
                        :value="p.id"
                      />
                    </el-select>
                  </el-form-item>
                </el-form>
              </template>

              <template v-else>
                <el-form label-width="100px" size="default" class="wf-form">
                  <el-form-item label="提供商">
                    <el-select v-model="row.provider" style="width: 220px">
                      <el-option
                        v-for="p in providerOptions"
                        :key="p.id"
                        :label="p.label"
                        :value="p.id"
                      />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="模型">
                    <el-input
                      v-model="row.model"
                      placeholder="模型 ID，如 anthropic/claude-opus-4.7"
                      style="width: 360px"
                    />
                    <div v-if="modelHints(row.provider).length" class="hints">
                      常用：
                      <el-tag
                        v-for="m in modelHints(row.provider)"
                        :key="m"
                        size="small"
                        class="hint-tag"
                        @click="row.model = m"
                      >
                        {{ m }}
                      </el-tag>
                    </div>
                  </el-form-item>
                  <el-form-item label="高级">
                    <el-input-number
                      v-model="row.max_tokens"
                      :min="256"
                      :max="200000"
                      :step="512"
                      controls-position="right"
                      placeholder="max_tokens"
                    />
                    <el-input-number
                      v-model="row.temperature"
                      :min="0"
                      :max="2"
                      :step="0.05"
                      :precision="2"
                      controls-position="right"
                      class="temp-input"
                      placeholder="temperature"
                    />
                  </el-form-item>
                </el-form>
              </template>

              <el-tag size="small" :type="row.source === 'database' ? 'success' : 'info'">
                {{ row.source === 'database' ? '已保存' : '出厂默认' }}
              </el-tag>
            </el-card>
          </template>
        </div>
      </el-tab-pane>

      <el-tab-pane label="敏感词审核" name="review">
        <el-alert
          type="info"
          :closable="false"
          show-icon
          class="hint"
          title="审核基于独立词库做敏感词检测。逐类开关：高危类默认开启，易误伤类（广告、民生等）默认关闭。生成内容会自动检测并标红，可在文章管理里用 AI 改写规避。"
        />
        <div class="toolbar">
          <el-button :icon="Refresh" @click="loadCategories" :loading="catLoading">刷新</el-button>
        </div>
        <div v-loading="catLoading" class="cat-grid">
          <el-card
            v-for="c in reviewCategories"
            :key="c.id"
            class="review-cat-card"
            shadow="never"
          >
            <div class="review-cat-row">
              <div class="review-cat-info">
                <b>{{ c.id }}</b>
                <span class="wf-key">{{ c.word_count }} 词</span>
              </div>
              <el-switch
                v-model="c.enabled"
                :loading="catSaving"
                @change="(v) => toggleCategory(c, v)"
              />
            </div>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane label="系统状态" name="status">
        <SystemStatus />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Check } from '@element-plus/icons-vue'
import { settingsApi, reviewApi } from '../api'
import SystemStatus from '../components/SystemStatus.vue'

export default {
  name: 'Settings',
  components: { SystemStatus },
  setup() {
    const activeTab = ref('llm')
    const loading = ref(false)
    const saving = ref(false)
    const items = ref([])
    const providerOptions = ref([])
    const providerHintsMap = ref({})

    // 敏感词分类开关
    const reviewCategories = ref([])
    const catLoading = ref(false)
    const catSaving = ref(false)

    const loadCategories = async () => {
      catLoading.value = true
      try {
        const res = await reviewApi.getCategories()
        reviewCategories.value = res.items || []
      } catch (e) {
        ElMessage.error('加载敏感词分类失败: ' + (e.message || ''))
      } finally {
        catLoading.value = false
      }
    }

    const toggleCategory = async (cat, value) => {
      catSaving.value = true
      try {
        await reviewApi.updateCategories({ [cat.id]: value })
        ElMessage.success(`已${value ? '启用' : '关闭'}「${cat.id}」`)
      } catch (e) {
        cat.enabled = !value
        ElMessage.error('保存失败: ' + (e.message || ''))
      } finally {
        catSaving.value = false
      }
    }

    const categories = computed(() => {
      const set = new Set()
      for (const it of items.value) {
        set.add(it.category || '其他')
      }
      return Array.from(set)
    })

    const itemsByCategory = (cat) =>
      items.value.filter(it => (it.category || '其他') === cat)

    const modelHints = (providerId) => providerHintsMap.value[providerId] || []

    const loadLlm = async () => {
      loading.value = true
      try {
        const [meta, data] = await Promise.all([
          settingsApi.getLlmMeta(),
          settingsApi.getLlmSettings(),
        ])
        providerOptions.value = meta.providers || []
        const hints = {}
        for (const p of providerOptions.value) {
          hints[p.id] = p.model_hints || []
        }
        providerHintsMap.value = hints

        const metaWf = {}
        for (const w of meta.workflows || []) {
          metaWf[w.workflow_key] = w
        }
        items.value = (data.items || []).map(it => {
          const m = metaWf[it.workflow_key] || {}
          const extra = { ...(it.extra_json || {}) }
          if (m.is_priority_list && !Array.isArray(extra.provider_priority)) {
            extra.provider_priority = ['dmx', 'openrouter', 'volcengine']
          }
          return {
            ...it,
            label: m.label || it.workflow_key,
            category: m.category || '其他',
            description: m.description || '',
            is_priority_list: !!m.is_priority_list,
            extra_json: extra,
          }
        })
      } catch (e) {
        ElMessage.error('加载失败: ' + (e.message || ''))
      } finally {
        loading.value = false
      }
    }

    const saveLlm = async () => {
      saving.value = true
      try {
        const payload = {
          items: items.value.map(row => ({
            workflow_key: row.workflow_key,
            provider: row.provider,
            model: row.model || '',
            max_tokens: row.max_tokens ?? null,
            temperature: row.temperature ?? null,
            extra_json: row.extra_json || {},
            is_enabled: row.is_enabled !== false,
          })),
        }
        await settingsApi.saveLlmSettings(payload)
        ElMessage.success('已保存，各流程将使用新配置')
        await loadLlm()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      } finally {
        saving.value = false
      }
    }

    onMounted(() => {
      loadLlm()
      loadCategories()
    })

    return {
      activeTab,
      loading,
      saving,
      items,
      providerOptions,
      categories,
      itemsByCategory,
      modelHints,
      loadLlm,
      saveLlm,
      reviewCategories,
      catLoading,
      catSaving,
      loadCategories,
      toggleCategory,
      Refresh,
      Check,
    }
  },
}
</script>

<style scoped>
.settings-page {
  padding: 4px 8px 24px;
}
.hint {
  margin-bottom: 16px;
}
.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.cat-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 18px 0 10px;
  padding-left: 4px;
  border-left: 3px solid #409eff;
}
.wf-card {
  margin-bottom: 12px;
}
.wf-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.wf-key {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
  font-family: monospace;
}
.wf-desc {
  color: #606266;
  font-size: 13px;
  margin: 0 0 10px;
}
.wf-form {
  margin-bottom: 8px;
}
.hints {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.hint-tag {
  margin: 2px 4px 2px 0;
  cursor: pointer;
}
.temp-input {
  margin-left: 12px;
}
.cat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.review-cat-card {
  margin-bottom: 0;
}
.review-cat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.review-cat-info b {
  font-size: 14px;
  color: #303133;
}
</style>
